// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xLocale from "@synd/XDL/Utilities/xValidate/xLocale";
import xMasks from "@synd/XDL/Utilities/xValidate/xMasks";
import { useFormik } from "formik";
import startCase from "lodash/startCase";
import { useMemo } from "react";
import * as Yup from "yup";

/**
 * set default language, xLocal file has the translations
 */
Yup.setLocale(xLocale);

/**
 * applyMask can be used to any text to apply mask immediately to it
 * @param {*} value
 * @param {*} schema
 * @param {} oldValue used only to regex on delete
 * @returns
 */
export const applyMask = (schema: string | any[], value: string, oldValue = "") => {
    const masks =
        typeof schema === "string"
            ? Object.entries(expand(schema)).map(([method, params]) => {
                  return { method, params };
              })
            : schema;
    return masks?.length
        ? masks.reduce((result, current) => {
              const transform = xMasks[current.method]?.transform;

              return transform ? transform(result, oldValue || "", ...current.params) : result;
          }, value)
        : value;
};

/**
 * useMask can be used on inputs to apply masks, note: its without validation checks just mask
 * @param {*} oldValue
 * @param {*} schema
 * @param {*} onChange
 * @returns
 */
export const useMask = (schema: string, oldValue: string, onChange: any) => {
    return (e: any) => {
        const newValue = e.target.value;
        const masked = applyMask(schema, newValue, oldValue);

        e.target.value = masked;

        return onChange?.(e, masked, newValue);
    };
};

/**
 * useValidate
 * @param {initialValues ,validators, onSubmit}
 * @returns { field, touched, errors, handleSubmit, isSubmitting, dirty }
 *
 * @note: each validators can be validator (and|or) mask, you can define your custom validations or masks in the xMasks file
 */

/** @usage
 const { field, touched, errors, handleSubmit, isSubmitting, dirty } =
 useValidate({
      initialValues: {
        firstName: "",
        lastName: "",
        phone: "",
      },
      // take look at what yup support or our custom masks, default type is string unless you change it
      validators: {
        firstName: "required|uppercase|min:1|max:10",
        lastName: "label:My Last Name|required|lowercase|min:2|max:10|noEmptySpace:force",
        phone: "required|phone",
      },
      onSubmit: (values, { setSubmitting }) => {
        setTimeout(() => {
          console.log(JSON.stringify(values, null, 2));
          setSubmitting(false);
        }, 5000);
      },
    });
 */

const defaultSchema = "max:30";

// extract validation string and convert it to functions
type expandType = { [key: string]: any[] | any };

const expand = (schemas: string, injectDefault: boolean | string = true): expandType => {
    const types = ["mixed", "string", "number", "boolean", "date", "array", "object"];
    if (injectDefault) {
        schemas = `${defaultSchema}|${schemas}`;

        // key passed, inject label
        if (typeof injectDefault === "string" && !schemas.includes("label")) {
            const label = startCase(injectDefault);
            schemas = `label:${label}|${schemas}`;
        }

        const hasType = types.some(type => schemas.includes(type));
        if (!hasType) {
            schemas = `string|${schemas}`;
        }
    }
    return schemas.split("|").reduce((result, schema) => {
        const ex = schema.split(":");
        result[ex[0]] = ex[1] ? ex[1].split(",") : [];
        return result;
    }, {} as expandType);
};

type UseValidateInterface = {
    initialValues: any;
    validators: { [key: string]: string };
    onSubmit: any;
};

export const useValidate = ({ initialValues, validators, onSubmit }: UseValidateInterface) => {
    /**
     * transform validators string into function invokes
     */
    const { validate: validateObject, mask: masksList } = useMemo(() => {
        const validate: expandType = {};
        const mask: expandType = {};

        // register Yup validations to be used in formik
        const registerValidate = (key: string, method: string, params: any[]) => {
            validate[key] = validate[key] || Yup;
            const canValidate = typeof validate[key][method] === "function";
            if (canValidate) {
                validate[key] = validate[key][method](...params);
            }
        };

        // extract validators per field
        Object.entries(validators).forEach(([key, schemas]) => {
            // extract validators schemas
            Object.entries(expand(schemas, key)).forEach(([method, params]) => {
                // inject Mask
                const isMask = typeof xMasks[method]?.transform === "function";
                if (isMask) {
                    if (!mask[key]) {
                        mask[key] = [];
                    }
                    mask[key].push({ method, params });

                    // inject extra mask validators to be default
                    if (xMasks[method]?.validate) {
                        Object.entries(expand(xMasks[method].validate!, false)).forEach(([method, params]) => {
                            registerValidate(key, method, params);
                        });
                    }
                }

                // inject Yup Schema Validation, this will override the mask validators in case of conflict
                registerValidate(key, method, params);
            });
        });
        return { validate, mask };
    }, [validators]);

    /**
     * formik injection
     */
    const formik = useFormik({
        initialValues,
        validationSchema: Yup.object(validateObject),
        onSubmit,
    });

    /**
     * fields auto injection
     */
    const field = (name: string) => {
        return {
            name,
            value: formik.values[name],
            onChange: (e: any) => {
                const key = e.target.name;
                const oldValue = formik.values[key];
                const newValue = e.target.value;
                const masked = applyMask(masksList[key], newValue, oldValue);
                formik.setFieldValue(key, masked);
                // formik.setFieldTouched(key, true);
            },
            onBlur: (e: any) => {
                formik.setFieldTouched(e.target.name, true);
            },
            disabled: formik.isSubmitting,
            error: (formik.touched[name] && formik.errors[name]) as any,
        };
    };

    return {
        formik,
        resetForm: formik.resetForm,
        values: formik.values,
        touched: formik.touched,
        dirty: formik.dirty,
        isSubmitting: formik.isSubmitting,
        errors: formik.errors,
        handleChange: formik.handleChange,
        handleBlur: formik.handleBlur,
        handleSubmit: formik.handleSubmit,
        handleReset: formik.handleReset,
        setFieldValue: formik.setFieldValue,
        field,
        masksList,
    };
};

export default useValidate;
